import { Component, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular';
import { exportDataGrid } from 'devextreme/excel_exporter';
import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { MaterialUIModule } from '@app/shared/modules/material-ui/material-ui.module';

export interface InternalNote {
  section?: string;
  cd_section?: string;
  section_code?: string;
  notes?: string;
  note_text?: string;
  added_by?: string;
  created_by_name?: string;
  created_by?: string | number;
  updated_date?: string | Date;
  [key: string]: any;
}

@Component({
  selector: 'app-internal-notes-modal',
  standalone: true,
  imports: [CommonModule, DxDataGridModule, MaterialUIModule],
  templateUrl: './internal-notes-modal.component.html',
  styleUrls: ['./internal-notes-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InternalNotesModalComponent {
  @Input() title: string = 'Internal Notes';
  @Input() notes: InternalNote[] = [];
  @Input() exportFileName: string = 'Internal Notes';

  @Input() activeTab: string = '';

  @ViewChild('internalNotesGrid', { static: false }) dataGrid!: DxDataGridComponent;

  showColumnChooser: boolean = false;

  /** Render '---' for empty/null/undefined cell values across every column. */
  readonly formatEmpty = (cellInfo: { value?: any; valueText?: string }): string => {
    const v = cellInfo?.value;
    const t = cellInfo?.valueText;
    if (v === null || v === undefined || v === '') return '---';
    if (typeof t === 'string' && t.trim() === '') return '---';
    return typeof t === 'string' ? t : String(v);
  };

  readonly calculateSectionValue = (rowData: InternalNote): string => {
    if (!rowData) return '';
    // If the parent already mapped to `section`, use it directly.
    if (rowData.section != null && rowData.section !== '') {
      return String(rowData.section);
    }
    const tab = (this.activeTab || '').trim();
    const caseManagementTabs = ['admin', 'analyst', 'intake', 'deficiency', 'doesNotQualify'];
    const useCdSection = tab === 'background' || caseManagementTabs.includes(tab);
    const value = useCdSection ? rowData.cd_section : rowData.section_code;
    return value != null ? String(value) : '';
  };

  allColumns: { key: string; label: string; disabled?: boolean }[] = [
    { key: 'section', label: 'Section', disabled: true },
    { key: 'notes', label: 'Notes' },
    { key: 'updated_date', label: 'Updated Date' },
    { key: 'added_by', label: 'Updated By' }
    
  ];

  visibleColumns: { [key: string]: boolean } = {
    section: true,
    notes: true,
    added_by: true,
    updated_date: true
  };

  constructor(public activeModal: NgbActiveModal) {}

  closeModal(): void {
    this.activeModal.dismiss();
  }

  exportToExcel(): void {
    if (!this.dataGrid?.instance) {
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Internal Notes');
    exportDataGrid({
      component: this.dataGrid.instance,
      worksheet
    }).then(() => {
      workbook.xlsx.writeBuffer().then(buffer => {
        FileSaver.saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          `${this.exportFileName}.xlsx`
        );
      });
    });
  }

  toggleColumnChooser(): void {
    this.showColumnChooser = !this.showColumnChooser;
  }

  closeColumnChooser(): void {
    this.showColumnChooser = false;
  }

  onColumnClick(col: { key: string; label: string; disabled?: boolean }): void {
    if (col.disabled) {
      // Section is locked-on; ensure it stays visible.
      this.visibleColumns[col.key] = true;
      return;
    }
    this.visibleColumns[col.key] = !this.visibleColumns[col.key];
  }

  onColumnKey(event: KeyboardEvent, col: { key: string; label: string; disabled?: boolean }): void {
    const k = event && event.key;
    if (k === 'Enter' || k === ' ') {
      event.preventDefault();
      this.onColumnClick(col);
    }
  }
}
